// @ts-nocheck
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, DollarSign, Clock } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import GraphicsPackageForm from "@/components/vendor-graphics/GraphicsPackageForm";
import VendorPackageOfferingsManager from "@/components/vendor-graphics/VendorPackageOfferingsManager";

interface GraphicsPackage {
  id: string;
  vendor_id: string;
  name: string;
  package_type: string;
  price: number;
  description?: string;
  features?: string[];
  is_active: boolean;
  created_at: string;
}

interface GraphicsVendorProfile {
  id: string;
  user_id: string;
  business_name?: string;
}

const VendorPackages: React.FC = () => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [packages, setPackages] = useState<GraphicsPackage[]>([]);
  const [vendorProfile, setVendorProfile] = useState<GraphicsVendorProfile | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<GraphicsPackage | null>(null);

  useEffect(() => {
    fetchData();
  }, [user?.id]);

  const fetchData = async () => {
    if (!user?.id) return;

    try {
      // Get vendor profile
      const { data: profileData, error: profileError } = await supabase
        .from('graphics_vendor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      setVendorProfile(profileData);

      // Get packages if we have a vendor profile
      if (profileData?.id) {
        const { data: packagesData, error: packagesError } = await supabase
          .from('graphics_packages')
          .select('*')
          .eq('vendor_id', profileData.id)
          .order('created_at', { ascending: false });

        if (packagesError) throw packagesError;
        setPackages(packagesData || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const handleEditPackage = (pkg: GraphicsPackage) => {
    setSelectedPackage(pkg);
    setShowForm(true);
  };

  const handleCreatePackage = () => {
    setSelectedPackage(null);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setSelectedPackage(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-gray-500">Loading packages...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Package Management
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Manage your graphics packages and offerings
        </p>
      </div>

      <Tabs defaultValue="offerings" className="w-full">
        <TabsList>
          <TabsTrigger value="offerings">Package Offerings</TabsTrigger>
          <TabsTrigger value="custom">Custom Packages</TabsTrigger>
        </TabsList>
        
        <TabsContent value="offerings" className="mt-6">
          <VendorPackageOfferingsManager />
        </TabsContent>
        
        <TabsContent value="custom" className="mt-6">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">Custom Packages</h3>
                <p className="text-sm text-muted-foreground">
                  Create custom packages outside of the standard offerings
                </p>
              </div>
              <Button onClick={handleCreatePackage}>
                <Plus className="h-4 w-4 mr-2" />
                Add Custom Package
              </Button>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="text-gray-500">Loading packages...</div>
              </div>
            ) : packages.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <p className="text-gray-500 mb-4">No custom packages created yet</p>
                  <Button onClick={handleCreatePackage}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Custom Package
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {packages.map((pkg) => (
                  <Card key={pkg.id} className="relative">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg">{pkg.name}</CardTitle>
                        <div className="flex gap-2">
                          <Badge variant={pkg.is_active ? "default" : "secondary"}>
                            {pkg.is_active ? "Active" : "Inactive"}
                          </Badge>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditPackage(pkg)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-500" />
                          <span className="font-semibold">{formatPrice(pkg.price)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-blue-500" />
                          <span className="text-sm text-gray-600">7 days</span>
                        </div>
                      </div>
                      
                      {pkg.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                          {pkg.description}
                        </p>
                      )}

                      {pkg.features && pkg.features.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Features:</p>
                          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                            {pkg.features.slice(0, 3).map((feature, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0"></span>
                                {feature}
                              </li>
                            ))}
                            {pkg.features.length > 3 && (
                              <li className="text-xs text-gray-500">
                                +{pkg.features.length - 3} more features
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {showForm && vendorProfile && (
        <GraphicsPackageForm
          package={selectedPackage}
          vendorId={vendorProfile.id}
          onPackageUpdate={fetchData}
          onCancel={handleCancelForm}
        />
      )}
    </div>
  );
};

export default VendorPackages;